import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, AlertCircle, CheckCircle2, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { superadminApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import { toast } from 'sonner';

/**
 * Superadmin → Request routing.
 *
 * The visual replacement for `scripts/show_request_routing.py`, which answered the most
 * common approvals support question there is — *"I booked leave three days ago and
 * nothing has happened"* — but only from a terminal, against one database, using a
 * command nobody remembers. Rob's rule: anything you'd actually want to look at needs a
 * screen.
 *
 * READ-ONLY by design. Where something's broken it links through to that employee's
 * record rather than growing its own fix-it buttons — one place to change things, which
 * is the same call the org chart's needs-attention strip made.
 */
const RequestRoutingManager = () => {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [onlyPending, setOnlyPending] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await superadminApi.listTenants();
        const rows = Array.isArray(list) ? list : (list?.tenants || []);
        setTenants(rows);
        if (rows.length && !tenantId) setTenantId(String(rows[0].id));
      } catch (e) {
        toast.error('Could not load the customer list');
      }
    })();
  // Runs once; the tenant list doesn't change while this screen is open.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (tid = tenantId) => {
    if (!tid) return;
    setLoading(true);
    try {
      const { data: res } = await superadminApi.getRequestRouting({
        tenant_id: tid, only_problems: onlyProblems, only_pending: onlyPending, limit: 50,
      });
      setData(res);
      // Anything with a problem starts open — you came here because something's wrong,
      // so making you click to find out which is just friction.
      setOpen(Object.fromEntries(
        (res.requests || []).filter((r) => r.problems.length).map((r) => [r.id, true])));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the routing report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) load(tenantId);
  }, [tenantId, onlyProblems, onlyPending]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="card-hive p-6">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Users className="w-4 h-4" /> Where leave requests actually went
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
          Reads the approval records written when each request was created — the decision
          the system actually made, not what it would decide today. Use it when a customer
          says a request has gone nowhere. Nothing here changes anything.
        </p>

        <div className="flex flex-wrap items-end gap-3 mt-5">
          <div>
            <Label className="text-xs text-muted-foreground">Customer</Label>
            <Select value={tenantId} onValueChange={setTenantId}>
              <SelectTrigger className="w-[240px] h-9 mt-1" data-testid="routing-tenant">
                <SelectValue placeholder="Pick a customer" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Show</Label>
            <Select value={onlyProblems ? 'problems' : 'all'} onValueChange={(v) => setOnlyProblems(v === 'problems')}>
              <SelectTrigger className="w-[180px] h-9 mt-1" data-testid="routing-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="problems">Needs attention</SelectItem>
                <SelectItem value="all">Everything</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={onlyPending ? 'pending' : 'any'} onValueChange={(v) => setOnlyPending(v === 'pending')}>
              <SelectTrigger className="w-[160px] h-9 mt-1" data-testid="routing-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending only</SelectItem>
                <SelectItem value="any">Any status</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => load()} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Reload
          </Button>
        </div>

        {data && (
          <div className={`mt-5 rounded-lg px-4 py-3 text-sm ${
            data.problem_count > 0
              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
              : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'}`}>
            {data.problem_count > 0
              ? <><strong>{data.problem_count}</strong> of the {data.shown} request{data.shown === 1 ? '' : 's'} shown can't be actioned by anyone, or would notify nobody.</>
              : <>Nothing needs attention — every request shown has somebody who can action it.</>}
          </div>
        )}
      </div>

      {loading && !data && (
        <div className="card-hive p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      )}

      {data && data.requests.length === 0 && (
        <div className="card-hive p-8 text-center text-muted-foreground">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
          <p className="font-medium text-foreground">Nothing to show</p>
          <p className="text-sm mt-1">
            {onlyProblems ? 'No routing problems for this customer.' : 'No matching leave requests.'}
          </p>
        </div>
      )}

      {(data?.requests || []).map((r) => {
        const isOpen = !!open[r.id];
        return (
          <div key={r.id} className="card-hive p-5" data-testid={`routing-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium">{r.employee} · {r.leave_type}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDateGB(r.start_date)}
                  {r.start_date !== r.end_date ? ` – ${formatDateGB(r.end_date)}` : ''}
                  {' · '}{r.status}
                  {r.total_levels > 1 ? ` · step ${r.current_level} of ${r.total_levels}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {r.problems.length > 0 ? (
                  <span className="text-xs bg-red-500/15 text-red-600 dark:text-red-400 px-2.5 py-1 rounded-full whitespace-nowrap">
                    Needs attention
                  </span>
                ) : (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400 whitespace-nowrap">Routed fine</span>
                )}
                <button
                  type="button"
                  onClick={() => setOpen((p) => ({ ...p, [r.id]: !p[r.id] }))}
                  className="text-sm text-primary hover:underline flex items-center gap-1 whitespace-nowrap"
                >
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {isOpen ? 'Hide' : 'Detail'}
                </button>
              </div>
            </div>

            {r.problems.map((p, i) => (
              <p key={i} className="mt-3 text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{p}</span>
              </p>
            ))}

            {isOpen && r.steps.map((s) => (
              <div key={s.level_order}
                   className={`mt-3 rounded-lg p-3 bg-secondary/40 ${s.is_current ? '' : 'opacity-70'}`}>
                <p className="text-sm font-medium">
                  Step {s.level_order}{s.level_name ? ` · ${s.level_name}` : ''} — {s.status}
                  {s.is_current && <span className="ml-2 text-xs text-primary">(here now)</span>}
                </p>
                {s.via_group && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Sent to the approval group <strong>{s.via_group}</strong>
                  </p>
                )}
                <div className="mt-2 space-y-1">
                  {s.candidates.map((c, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 text-sm">
                      <span>{c.name || '(nobody)'}</span>
                      <span className={c.can_approve
                        ? (c.reason ? 'text-amber-600 dark:text-amber-500 text-right' : 'text-emerald-600 dark:text-emerald-400')
                        : 'text-red-600 dark:text-red-400 text-right'}>
                        {c.reason || 'can approve'}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                  {s.backup_name && <p>Escalates to {s.backup_name}</p>}
                  {s.reminders_sent > 0 && <p>{s.reminders_sent} reminder{s.reminders_sent === 1 ? '' : 's'} sent</p>}
                  {s.escalated_at && <p>Handed to the admins on {formatDateGB(s.escalated_at)}</p>}
                  {s.actioned_by && <p>Actioned by {s.actioned_by}{s.actioned_at ? ` on ${formatDateGB(s.actioned_at)}` : ''}</p>}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default RequestRoutingManager;
